import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import { DriverSummary } from '../types';
import { SortDirection, getSortIndicator, sortRows } from '../utils/sort';

const DriversPage: React.FC = () => {
  const navigate = useNavigate();
  const [drivers, setDrivers] = useState<DriverSummary[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<{ key: 'name' | 'nation' | 'sessions' | 'lastSeen'; direction: SortDirection }>({ key: 'sessions', direction: 'desc' });

  const loadDrivers = async (query?: string) => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.getDrivers(query?.trim() || undefined);
      setDrivers(res.data.drivers || []);
    } catch (err) {
      console.error('Failed to load drivers:', err);
      setError('Unable to load driver list.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDrivers();
  }, []);

  const handleSort = (key: 'name' | 'nation' | 'sessions' | 'lastSeen') => {
    setSortConfig((current) => {
      if (current.key === key) {
        return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const sortedDrivers = useMemo(() => sortRows(drivers, sortConfig.direction, (driver) => {
    switch (sortConfig.key) {
      case 'nation':
        return driver.nation;
      case 'sessions':
        return driver.sessionCount;
      case 'lastSeen':
        return driver.lastSeen;
      case 'name':
      default:
        return driver.firstName && driver.lastName ? `${driver.firstName} ${driver.lastName}` : driver.driverName;
    }
  }), [drivers, sortConfig]);

  return (
    <div>
      <div className="page-header">
        <h1>Drivers</h1>
        <p>Search every driver with archived results and view their profile summary.</p>
      </div>

      <div className="section-card" style={{ marginBottom: '24px' }}>
        <div className="form-row">
          <div>
            <label>Search drivers</label>
            <input
              type="text"
              placeholder="Driver name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  loadDrivers(search);
                }
              }}
            />
          </div>
          <div style={{ alignSelf: 'end' }}>
            <button className="primary-button" onClick={() => loadDrivers(search)} disabled={loading}>
              Search
            </button>
          </div>
        </div>

        {loading && <div className="empty-state">Loading drivers...</div>}
        {error && <div className="empty-state">{error}</div>}

        {!loading && !error && (
          <div className="table-wrapper">
            <table className="drivers-condensed">
              <thead>
                <tr>
                  <th><button type="button" onClick={() => handleSort('name')} style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', cursor: 'pointer', font: 'inherit', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>Name <span aria-hidden="true">{getSortIndicator(sortConfig.key, 'name', sortConfig.direction)}</span></button></th>
                  <th><button type="button" onClick={() => handleSort('nation')} style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', cursor: 'pointer', font: 'inherit', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>Nation <span aria-hidden="true">{getSortIndicator(sortConfig.key, 'nation', sortConfig.direction)}</span></button></th>
                  <th><button type="button" onClick={() => handleSort('sessions')} style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', cursor: 'pointer', font: 'inherit', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>Sessions <span aria-hidden="true">{getSortIndicator(sortConfig.key, 'sessions', sortConfig.direction)}</span></button></th>
                  <th><button type="button" onClick={() => handleSort('lastSeen')} style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', cursor: 'pointer', font: 'inherit', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>Last seen <span aria-hidden="true">{getSortIndicator(sortConfig.key, 'lastSeen', sortConfig.direction)}</span></button></th>
                </tr>
              </thead>
              <tbody>
                {sortedDrivers.map((driver) => (
                  <tr key={driver.id || driver.driverId} style={{ cursor: 'pointer' }} onClick={() => navigate(`/drivers/${driver.id || driver.driverId}`)}>
                    <td>
                      {driver.firstName && driver.lastName
                        ? `${driver.firstName} ${driver.lastName}`
                        : driver.driverName}
                    </td>
                    <td>{driver.nation}</td>
                    <td>{driver.sessionCount}</td>
                    <td>{driver.lastSeen ? new Date(driver.lastSeen).toLocaleDateString() : 'Unknown'}</td>
                  </tr>
                ))}
                {drivers.length === 0 && (
                  <tr>
                    <td colSpan={4} className="empty-state">No drivers found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default DriversPage;
